import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Hourglass, Building2, Camera, Check, ClipboardList, FileText, Mail, MapPin, MessagesSquare, RotateCcw, RotateCw, Timer, Users, Wrench } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { useScrollOnOpen } from '../lib/useScrollOnOpen';
import { ClaimInfoHeader, FlowBadges, RowHousePhoto, daysAt, type FlowRow } from './AcceptedFlowShared';
import { FollowUpPanel, diasSinFollowUp, type FollowUpInfo } from './FollowUpPanel';
import { ClaimChat } from './ClaimChat';
import { ClaimAppointmentsPanel } from './ClaimAppointmentsPanel';
import { ClaimAtrBox } from './ClaimAtrBox';
import { ClaimEmailBox } from './ClaimEmailBox';
import { CaseOverview } from './CaseOverview';
import { ClaimFiles } from './ClaimFiles';
import { Module } from './ui/Module';
import { RChip } from './ui/RChip';
import { DecisionBar } from './ui/DecisionBar';

/** Botones de decisión del depto de Claims (Waiting approval) — DecisionBar del rediseño:
 *  SOLO los botones que mueven el claim de etapa llevan color sólido fuerte. */
function DecisionButtons({ claimId, onChanged }: { claimId: string; onChanged: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const decide = async (decision: string, label: string) => {
    if (!window.confirm(`${t('cwa.confirm')} "${label}"?`)) return;
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/claims-decision`, { decision }); setMsg(t('cwa.decided')); onChanged(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  };
  return (
    <div className="module">
      <DecisionBar label={t('cwa.decision')} hint={msg || undefined}>
        <button className="btn ghost sm" disabled={busy} onClick={() => decide('needs_reschedule', t('cwa.needReschedule'))}><RotateCw size={14} strokeWidth={1.75} /> {t('cwa.needReschedule')}</button>
        <button className="btn ghost sm" disabled={busy} onClick={() => decide('reinspection', t('cwa.reinspection'))}><RotateCcw size={14} strokeWidth={1.75} /> {t('cwa.reinspection')}</button>
        <button className="btn ghost sm" disabled={busy} onClick={() => decide('atr_request', t('cwa.atrRequest'))}><FileText size={14} strokeWidth={1.75} /> {t('cwa.atrRequest')}</button>
        <button className="btn sm solid-good" disabled={busy} onClick={() => decide('approved', t('cwa.approved'))}><Check size={14} strokeWidth={1.75} /> {t('cwa.approved')}</button>
        <button className="btn sm solid-warn" disabled={busy} onClick={() => decide('approved_repairs', t('cwa.approvedRepair'))}><Wrench size={14} strokeWidth={1.75} /> {t('cwa.approvedRepair')}</button>
      </DecisionBar>
    </div>
  );
}

/** La fila, vista como seguimiento — misma forma para el panel y para el orden de la cola. */
const fu = (r: FlowRow): FollowUpInfo => ({
  claim_id: r.claim_id, status_since: r.status_since,
  next_follow_up_at: r.next_follow_up_at, next_follow_up_calc: r.next_follow_up_calc,
  last_follow_up_at: r.last_follow_up_at, last_follow_up_by: r.last_follow_up_by,
  last_follow_up_by_id: r.last_follow_up_by_id,
});

function WaitingRow({ r, onChanged, onClient, view }: { r: FlowRow; onChanged: () => void; onClient: (id: string) => void; view: 'cards' | 'lines' }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:wa:${r.claim_id}`, false);
  const rowRef = useScrollOnOpen<HTMLDivElement>(open);
  const days = daysAt(r.status_since);
  /**
   * ANTIGÜEDAD REAL — la que el backfill escondía.
   *
   * El chip de `days` cuenta desde `status_since`, y esa fecha está backfilleada en 572 de 1405
   * claims (sellados todos el 30-jun/2-jul). Por eso la pantalla mostraba un tope de 49 días y
   * ~25 claims empatados en 47: parecía una cola sana. La real tiene mediana de 53 días, máximo
   * de 159, y 16 claims por encima de los 90.
   *
   * `waiting_approval_on` viene del board de Monday (88,8% de cobertura). Se muestra APARTE del
   * chip viejo, no en su lugar: son dos números distintos y mezclarlos volvería a esconder el
   * problema. Cuando el board no cubre el claim es null y no se dibuja nada — un hueco visible
   * es mejor que un número inventado.
   */
  const diasReales = daysAt(r.waiting_approval_on ?? null);
  const sev = diasReales == null ? null : diasReales >= 90 ? 'no' : diasReales >= 60 ? 'warn' : diasReales >= 30 ? 'warn' : 'info';
  return (
    <div ref={rowRef} className="card" style={{ padding: 12, scrollMarginTop: 80 }}>
      {view === 'cards' && <RowHousePhoto url={r.photo_url} />}
      {/* Cabecera colapsada: nombre + #claim + seguro (todo a la izquierda), equipo, días, fotos, badges. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setOpen((o) => !o)} style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, marginTop: 2, font: 'inherit' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            {/* Click en el nombre → tarjeta lateral con el resumen completo. */}
            <button onClick={() => onClient(r.claim_id)} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--ink)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>              {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
            </button>
            {r.claim_number && <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', textDecoration: 'none' }}>{r.claim_number}</Link>}
            {r.insurance_company && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--bg)', color: 'var(--ink)' }}><Building2 size={12} strokeWidth={1.75} /> {r.insurance_company}</span>}
          </div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>
            {r.contractor_name || '—'}{r.address ? <> · <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</> : ''}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: 'var(--accent-soft)', color: 'var(--accent)' }}><Users size={12} strokeWidth={1.75} /> {r.assigned_team_name || '—'}</span>
            {days != null && <RChip variant={days >= 7 ? 'no' : 'info'} icon={Timer}>{days}d</RChip>}
            {diasReales != null && (
              <RChip variant={sev as 'no' | 'warn' | 'info'} icon={Hourglass} title={t('cwa.realAgeHelp')}>
                {t('cwa.realAge', { d: String(diasReales) })}
              </RChip>
            )}
            {r.photos_url
              ? <a href={r.photos_url} target="_blank" rel="noopener noreferrer" className="btn ghost sm" onClick={(e) => e.stopPropagation()}><Camera size={14} strokeWidth={1.75} /> {t('cwa.photos')}</a>
              : <RChip icon={Camera}>{t('cwa.noPhotos')}</RChip>}
          </div>
          {/* Todos los badges (welcome call / intake / fotos / contrato / tipo) — SOLO acá (no repetir al desplegar). */}
          <div style={{ marginTop: 8 }}><FlowBadges r={r} /></div>
        </div>
        {/* EL SEGUIMIENTO, EN COLUMNA A LA DERECHA (Manuel 2026-08-20: "lo quiero a la derecha
            vertical pegado a la derecha, que tengo mucho espacio en blanco por allá"). Estaba
            en franja horizontal abajo: ocupaba una línea entera de la tarjeta y dejaba medio
            ancho vacío. Fuera de la columna de contenido para que la fila lo empuje al borde;
            en pantalla angosta el flexWrap del padre lo baja solo. */}
        <div style={{ marginLeft: 'auto', paddingLeft: 12, borderLeft: '1px dashed var(--line)', alignSelf: 'stretch' }}>
          <FollowUpPanel
            r={fu(r)}
            deptKey="claims" onChanged={onChanged} vertical />
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 12, display: 'grid', gap: 12 }}>
          {/* 1º info del claim + fotos casa/techo (sin repetir el nombre). El follow-up se
              unificó en Department Updates (ClaimChat) — ya no hay módulo aparte. */}
          <ClaimInfoHeader claimId={r.claim_id} hideName />
          {/* Módulos del rediseño, recogidos por defecto (pedido del dueño: abierto empujaba
              todo hasta abajo) — con preview de una línea para saber qué hay adentro. */}
          <Module id={`updates:${r.claim_id}`} icon={MessagesSquare} title={t('cwa.updates')} preview={t('mod.updatesPreview')} defaultOpen={false}>
            <ClaimChat claimId={r.claim_id} channel="claims" title={t('cwa.updates')} />
          </Module>
          <Module id={`inspection:${r.claim_id}`} icon={ClipboardList} title={t('cwa.inspectionReport')} preview={t('mod.inspectionPreview')} defaultOpen={false}>
            <div style={{ display: 'grid', gap: 12, paddingTop: 2 }}>
              <ClaimAppointmentsPanel claimId={r.claim_id} clientName={r.homeowner_name} />
              <ClaimAtrBox claimId={r.claim_id} />
            </div>
          </Module>
          <Module id={`email-overview:${r.claim_id}`} icon={Mail} title={t('cwa.emailOverview')} preview={t('mod.emailPreview')} defaultOpen={false}>
            <div style={{ display: 'grid', gap: 12, paddingTop: 2 }}>
              <ClaimEmailBox claimId={r.claim_id} departmentKey="claims" />
              <CaseOverview claimId={r.claim_id} readOnly />
            </div>
          </Module>
          {/* Files: solo subir (para ver los files se abre el claim → Files). */}
          <ClaimFiles claimId={r.claim_id} onEstimate={onChanged} uploadOnly />
          <DecisionButtons claimId={r.claim_id} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

/**
 * LA FORMA REAL DE LA COLA, ARRIBA DE TODO.
 *
 * El chip por fila ya mostraba la antigüedad real, pero para verla había que recorrer la lista.
 * Este resumen la pone de una: mediana, máximo y cuántos pasaron los 90 días.
 *
 * POR QUÉ IMPORTA QUE ESTÉ ACÁ: con la fecha backfilleada la cola se veía sana —tope de 49 días,
 * ~25 claims empatados en 47— y nadie miraba más. La real tiene mediana de 42 días, máximo de 158
 * y 14 claims por encima de los 90. Ninguno de esos catorce era visible sin abrir fila por fila.
 *
 * LA COBERTURA VIAJA CON EL NÚMERO. La mediana se calcula sobre los claims que el board cubre
 * (~88%); decir "42 días" sin decir sobre cuántos es la clase de número que después nadie puede
 * defender. Si la cobertura cae, el aviso aparece solo.
 */
function ResumenDeLaCola({ rows }: { rows: FlowRow[] }) {
  const { t } = useI18n();
  const dias = rows
    .map((r) => daysAt(r.waiting_approval_on ?? null))
    .filter((d): d is number => d != null)
    .sort((a, b) => a - b);
  if (!dias.length) return null;

  const p = (q: number) => dias[Math.min(dias.length - 1, Math.floor(q * dias.length))]!;
  const mediana = p(0.5);
  const maximo = dias[dias.length - 1]!;
  const mas90 = dias.filter((d) => d >= 90).length;
  const cobertura = Math.round((100 * dias.length) / rows.length);

  const Cifra = ({ v, l, alerta }: { v: string; l: string; alerta?: boolean }) => (
    <div style={{ display: 'grid', gap: 2, minWidth: 88 }}>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)',
        color: alerta ? 'var(--red)' : 'var(--ink)', lineHeight: 1.1,
      }}>{v}</span>
      <span className="muted" style={{ fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{l}</span>
    </div>
  );

  return (
    <div className="card" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start', padding: 12 }}>
      <Cifra v={String(rows.length)} l={t('cwa.sumInQueue')} />
      <Cifra v={t('cwa.sumDays', { d: String(mediana) })} l={t('cwa.sumMedian')} />
      <Cifra v={t('cwa.sumDays', { d: String(maximo) })} l={t('cwa.sumMax')} alerta={maximo >= 90} />
      <Cifra v={String(mas90)} l={t('cwa.sumOver90')} alerta={mas90 > 0} />
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', flex: '1 1 220px', lineHeight: 1.5 }}>
        {t('cwa.sumCoverage', { n: String(dias.length), total: String(rows.length), pct: String(cobertura) })}
      </div>
    </div>
  );
}

/** Waiting approval (depto Claims): filas ricas con reporte de inspección, ATR, estimado IA y decisiones. */
export function ClaimsWaitingApprovalSection(
  { rows, onChanged, onClient, view = 'lines' }: { rows: FlowRow[]; onChanged: () => void; onClient: (id: string) => void; view?: 'cards' | 'lines' },
) {
  const { t } = useI18n();
  /**
   * PRIMERO EL MÁS ABANDONADO — pedido de Manuel (2026-08-20): "quiero que muestre el que tiene
   * más días sin follow up primero y luego los demás en orden de mayor a menor en días".
   * La cuenta es LA MISMA del panel (`diasSinFollowUp`), no una copia: si algún día cambia lo que
   * significa "sin follow-up", el orden y el número que se ve en la fila no se separan.
   */
  const ordenadas = [...rows].sort((a, b) => diasSinFollowUp(fu(b)) - diasSinFollowUp(fu(a)));
  if (rows.length === 0) return <div className="page-empty">{t('cwa.empty')}</div>;
  const wrap: React.CSSProperties = view === 'cards'
    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 12, alignItems: 'start' }
    : { display: 'grid', gap: 10 };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <ResumenDeLaCola rows={rows} />
      <div style={wrap}>{ordenadas.map((r) => <WaitingRow key={r.presence_id} r={r} onChanged={onChanged} onClient={onClient} view={view} />)}</div>
    </div>
  );
}
